// RehearsalProgress.js
import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Clock, TrendingUp, ArrowRight } from 'lucide-react';
import styles from './RehearsalProgress.module.css';
import { Link } from 'react-router-dom';

const RehearsalProgress = ({ rehearsalData, onTabChange }) => {
    // Check if there's no data or if it's not a rehearsal and has no related rehearsals
    const hasNoData = !rehearsalData || (!rehearsalData.is_rehearsal && !rehearsalData.related_rehearsals?.length);

    const handleImproveClick = () => {
        if (onTabChange) {
            onTabChange('improve');
        }
    };

    if (hasNoData) {
        return (
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}>
                        <Clock className={styles.icon} />
                        <span>Improvement Progress</span>
                    </div>
                </div>
                <div className={`${styles.cardContent} ${styles.emptyState}`}>
                    <div className={styles.emptyStateContent}>
                        <h3 className={styles.emptyStateTitle}>Keep Practicing to Improve</h3>
                        <p className={styles.emptyStateDescription}>
                            This is where you'll see your improvement over time of this specific practice. Start improving with AI recommendations and track your progress!
                        </p>
                        <button onClick={handleImproveClick} className={styles.startButton}>
                            Start Improving
                            <ArrowRight className={styles.buttonIcon} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Rest of the existing component logic
    const chartData = [];

    if (rehearsalData.original_presentation) {
        chartData.push({
            id: rehearsalData.original_presentation.id,
            name: 'Original',
            score: rehearsalData.original_presentation.overall_score,
            date: new Date(rehearsalData.original_presentation.created_at)
        });
    }

    rehearsalData.related_rehearsals.forEach((rehearsal, index) => {
        chartData.push({
            id: rehearsal.id,
            name: `Rehearsal ${index + 1}`,
            score: rehearsal.overall_score,
            date: new Date(rehearsal.created_at)
        });
    });

    chartData.sort((a, b) => a.date - b.date);

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                    <Clock className={styles.icon} />
                    <span>Improvement Progress</span>
                </div>
                {chartData.length > 0 && (
                    <div className={styles.scoreChange}>
                        <TrendingUp className={styles.icon} />
                        <span>
                            Improvement: {' '}
                            <span className={`${styles.scoreValue} ${chartData[chartData.length - 1].score - chartData[0].score > 0
                                ? styles.positive
                                : styles.negative
                                }`}>
                                {(chartData[chartData.length - 1].score - chartData[0].score).toFixed(1)}%
                            </span>
                        </span>
                    </div>
                )}
            </div>
            <div className={styles.cardContent}>
                <div className={styles.chartContainer}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12 }}
                                interval={0}
                            />
                            <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        return (
                                            <div className={styles.tooltip}>
                                                <p className={styles.tooltipTitle}>{payload[0].payload.name}</p>
                                                <p className={styles.tooltipScore}>
                                                    Score: {payload[0].value.toFixed(1)}%
                                                </p>
                                                <p className={styles.tooltipDate}>
                                                    {payload[0].payload.date.toLocaleDateString()}
                                                </p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="score"
                                stroke="#0ea5e9"
                                strokeWidth={2}
                                dot={{
                                    strokeWidth: 2,
                                    r: 4,
                                    strokeColor: "#0ea5e9",
                                    fill: "white"
                                }}
                                activeDot={{
                                    r: 6,
                                    strokeWidth: 2
                                }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className={styles.sessionsContainer}>
                    <h4 className={styles.sessionsTitle}>Practice Sessions</h4>
                    <div className={styles.sessionsGrid}>
                        {chartData.map((session) => (
                            <Link
                                key={session.id}
                                to={`/presentations/${session.id}`}
                                className={styles.sessionItem}
                            >
                                <span className={styles.sessionName}>{session.name}</span>
                                <span className={styles.sessionScore}>{session.score.toFixed(1)}%</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RehearsalProgress;